import { toOffsetAwareDate } from "./entitySchemas";

export const DEFAULT_EVENT_TIME_ZONE = "UTC";

const resolvedTimeZones = new Map<string, string>();
const FIXED_OFFSET_TIME_ZONE = /^[+-]\d{2}(?::?\d{2})?$/;

export function resolveEventTimeZoneCandidate(
  ...candidates: unknown[]
): string | null {
  for (const candidate of candidates) {
    if (typeof candidate !== "string" || !candidate.trim()) continue;

    const normalized = candidate.trim();
    if (FIXED_OFFSET_TIME_ZONE.test(normalized)) continue;
    const cached = resolvedTimeZones.get(normalized);
    if (cached) return cached;

    try {
      const resolved = new Intl.DateTimeFormat("en-US", {
        timeZone: normalized,
      }).resolvedOptions().timeZone;
      resolvedTimeZones.set(normalized, resolved);
      return resolved;
    } catch {
      // Try the next source before falling back to UTC.
    }
  }

  return null;
}

export function resolveEventTimeZone(...candidates: unknown[]): string {
  return (
    resolveEventTimeZoneCandidate(...candidates) ?? DEFAULT_EVENT_TIME_ZONE
  );
}

export function toEventDate(
  value: string | null | undefined,
  timeZone: string,
): Date | null {
  const normalized = toOffsetAwareDate(value, timeZone);
  if (!normalized) return null;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function eventCalendarDateKey(
  value: Date | string | null | undefined,
  timeZone: string,
): string | null {
  const date =
    value instanceof Date ? value : toEventDate(value ?? undefined, timeZone);
  if (!date || Number.isNaN(date.getTime())) return null;

  const values = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      calendar: "gregory",
      numberingSystem: "latn",
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  const { year, month, day } = values;
  return year && month && day ? `${year}-${month}-${day}` : null;
}

export function formatEventCalendarDate(
  value: string | null | undefined,
  timeZone: string,
): string | null {
  const key = eventCalendarDateKey(value, timeZone);
  if (!key) return null;

  const [year, month, day] = key.split("-");
  return `${day}.${month}.${year}`;
}

export function formatEventDateInTimeZone(
  value: string | null | undefined,
  timeZone: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string | null {
  const date = toEventDate(value, timeZone);
  if (!date) return null;

  return new Intl.DateTimeFormat(locale, {
    calendar: "gregory",
    numberingSystem: "latn",
    ...options,
    timeZone,
  }).format(date);
}
